import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { AudioStream } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState, Row } from '@tanstack/react-table'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  NewDialog,
  RowActions,
  useColumns,
  SelectionToolbarActions,
  LifecyclePolicyDialog,
} from 'domains/Workspaces/Audio/Streams'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { audioStreamsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams'
import { audioStreamGeneralRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams/Stream'
import { audioStreamQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [streamData, setStreamData] = useState<AudioStream | AudioStream[] | undefined>(undefined)

  const tableInstanceRef = useRef<ReactTableType<AudioStream>>(undefined)

  const navigate = useNavigate({ from: audioStreamsOverviewRoute.fullPath })
  const { workspaceId } = audioStreamsOverviewRoute.useParams()
  const { query, groups, excludeGrouped } = audioStreamsOverviewRoute.useSearch()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const audioStreamsQuery = useInfiniteQuery({
    ...audioStreamQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId as string,
      query,
      groups,
      excludeGrouped,
    }),
    enabled: workspaceQuery.isSuccess && !!workspaceQuery.data.contentRepositoryId,
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const audioStreams = audioStreamsQuery.data?.flat

  const columns = useColumns()

  const handleLifecyclePolicyClick = useCallback((selectedRows: Row<AudioStream>[]) => {
    const streamData = selectedRows.map((row) => row.original)
    setStreamData(streamData)
  }, [])

  useEffect(() => {
    if (audioStreamsQuery.isSuccess) {
      tableInstanceRef.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = audioStreams?.find((stream) => stream.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [audioStreams, audioStreamsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Audio.audioStreamSearch')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: audioStreamQueryKeys.list({
                  repositoryId: workspace?.contentRepositoryId,
                  query,
                  groups,
                  excludeGrouped,
                }).queryKey,
              }),
            [excludeGrouped, groups, query, queryClient, workspace?.contentRepositoryId],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>
      <Table
        getTableInstance={(tableInstance) => void (tableInstanceRef.current = tableInstance)}
        size={48}
        data={audioStreams ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || audioStreamsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={audioStreamsOverviewRoute.fullPath}
            to={audioStreamGeneralRoute.to}
            params={{ audiostreamId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(audioStreamsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.audioStream', { count: 1 }),
        })}
        RowActions={(props) => <RowActions onClick={(row) => setStreamData(row.original)} {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.audioStream', { count: 2 })}
            discardSelection={() => tableInstanceRef.current?.resetRowSelection()}
            Actions={
              tableInstanceRef.current && (
                <SelectionToolbarActions
                  onLifecyclePolicyClick={() => handleLifecyclePolicyClick(selectedRows)}
                  selectedRows={selectedRows}
                  tableInstance={tableInstanceRef.current}
                />
              )
            }
          />
        )}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>

      <Dialog open={!!streamData} onClose={() => setStreamData(undefined)}>
        {streamData ? (
          <LifecyclePolicyDialog
            streamData={streamData}
            onSuccess={() => tableInstanceRef.current?.resetRowSelection()}
          />
        ) : null}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
